import React, { ReactNode, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { AiOutlineClose } from "react-icons/ai";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  width: string;
  showCloseButton?: boolean;
  closeBtnSize?: string;
  closeButtonColor?: string;
  isClosedWhenClickOutside?: boolean;
}
const PopUpModel: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  width,
  showCloseButton = true,
  closeBtnSize,
  closeButtonColor,
  isClosedWhenClickOutside = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleOutsideClick = (e: MouseEvent) => {
    if (
      isClosedWhenClickOutside &&
      modalRef.current &&
      !modalRef.current.contains(e.target as Node)
    ) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Rendered through a portal to <body> so it centers against the viewport,
  // regardless of any ancestor with transform / filter / backdrop-filter.
  return ReactDOM.createPortal(
    <div className="login_modal fixed inset-0 z-[1000] flex w-full items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative max-h-[90vh] w-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f12] px-6 py-5 shadow-[0_30px_80px_rgba(0,0,0,0.6)] scrollbar-hide"
          style={{ width: `${width}`, maxWidth: "95vw" }}
        >
          {showCloseButton && (
            <button
              className="absolute right-4 top-4 z-10 text-gray-400 transition-colors hover:text-white"
              onClick={onClose}
              aria-label="Close"
            >
              <AiOutlineClose
                className={`font-extrabold ${
                  closeBtnSize ? `text-[${closeBtnSize}]` : `text-[16px]`
                } ${closeButtonColor ? `hover:text-[${closeButtonColor}]` : ""}`}
              />
            </button>
          )}
          <div className="mt-2">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PopUpModel;